import axios from "axios";
import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchOrganizationList,
  login,
  loginWithGoogle,
  CreateOrganization,
  fetchOrganizationDetails,
  CreateUser,
  getUserAccountDetails,
  fetchOrganizationHierarchy,
  fetchOrganizationSummary,
} from "../../services/OrganizationService";
import { Pagination } from "../../types/CommonType";
import { fetchUserStats } from "../../services/OrganizationService";

const extractErrorMeta = (error: unknown) => {
  const fallback = "An unexpected error occurred. Please try again.";

  const statusFallbackMessages: Record<number, string> = {
    429: "Too many failed login attempts. Please try again later.",
    423: "Account temporarily locked due to too many failed login attempts. Please try again later.",
    409: "Conflict detected. Please verify your request and try again.",
    403: "Invalid Username/Password.",
  };

  const getResponseMessage = (responseData: any) => {
    if (!responseData) return null;
    if (typeof responseData === "string") return responseData;
    if (typeof responseData.msg === "string") return responseData.msg;
    return null;
  };

  const getFallbackMessage = (status?: number) => {
    if (status && statusFallbackMessages[status]) {
      return statusFallbackMessages[status];
    }
    return fallback;
  };

  let message = fallback;
  let retryAfterSeconds: number | null = null;

  if (axios.isAxiosError(error)) {
    const responseData = error.response?.data;
    const responseMessage = getResponseMessage(responseData);
    const statusCode = error.response?.status;

    if (responseMessage) {
      message = responseMessage;
    } else {
      message = getFallbackMessage(statusCode);
    }

    if (statusCode === 429 || statusCode === 423) {
      retryAfterSeconds = Number(responseData?.retry_after_seconds ?? responseData?.retryAfterSeconds ?? null) || null;
      if (retryAfterSeconds === null) {
        const parsedMinutes = responseMessage?.match(/(\d+)\s*minutes?/i) ?? responseMessage?.match(/(\d+)\s*mins?/i);
        if (parsedMinutes && parsedMinutes[1]) {
          retryAfterSeconds = Number(parsedMinutes[1]) * 60;
        }
      }
    }

    if (!responseMessage && !message && error.message) {
      message = error.message;
    }
  } else if (error instanceof Error) {
    message = error.message || fallback;
  }

  return { message, retryAfterSeconds };
};

export const fetchOrganizationListAsync = createAsyncThunk(
  "user/fetchOrganizations",
  async (
    { page, per_page, orgId }: Pagination,
    thunkAPI
  ) => {
    let response: any;
    if (orgId) {
      response = await fetchOrganizationList(page, per_page, orgId);
      response.orgId = orgId;
    } else {
      response = await fetchOrganizationList(page, per_page);
    }
    return response;
  }
);

export const fetchOrganizationDetailsAsync = createAsyncThunk(
  "user/fetchOrgDetails",
  async (orgId: number) => {
    const response: any = await fetchOrganizationDetails(orgId);
    return response;
  }
);

export const loginUserAsync = createAsyncThunk(
  "user/Login",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await login(data);
      return response;
    } catch (error) {
      const { message, retryAfterSeconds } = extractErrorMeta(error);
      return rejectWithValue({ message, retryAfterSeconds });
    }
  }
);

export const loginWithGoogleAsync = createAsyncThunk(
  "user/LoginWithGoogle",
  async (idToken: string, { rejectWithValue }) => {
    try {
      const response = await loginWithGoogle(idToken);
      return response;
    } catch (error:any) {
      console.error("Error in loginWithGoogleAsync:", error);
      return rejectWithValue(error?.response?.data?.msg || "Google login failed");
    }
  }
);

export const createOrganizationAsync = createAsyncThunk(
  "user/CreateOrg",
  async (data: any, thunkAPI) => {
    const response = await CreateOrganization(data);
    return response;
  }
);

export const createUserAsync = createAsyncThunk(
  "user/CreateUser",
  async (data: any, { rejectWithValue }) => {
    try {
      return await CreateUser(data);
    } catch (error: any) {
      console.error("Error in createUserAsync:", error);
      return rejectWithValue(error?.message);
    }
  }
);

export const getUserAccountDetailsAsync = createAsyncThunk(
  "user/getUserAccountDetails",
  async () => {
    const response: any = await getUserAccountDetails();
    return response;
  }
);

export const fetchOrganizationHierarchyAsync = createAsyncThunk(
  "user/fetchOrganizationHierarchy",
  async (orgId: number) => {
    const response: any = await fetchOrganizationHierarchy(orgId);
    return response;
  }
);



export const fetchOrganizationSummaryAsync = createAsyncThunk(
  "user/fetchOrganizationSummary",
  async (orgId: number) => {
    const response: any = await fetchOrganizationSummary(orgId);
    return response;
  }
);

export const fetchUserStatsAsync = createAsyncThunk(
  "user/fetchUserStats",
  async () => {
    const response = await fetchUserStats();
    return response;
  }
);